import { useRef, useState } from "react";
import {
	m,
	useMotionTemplate,
	useMotionValueEvent,
	useMotionValue,
	useReducedMotion,
	useScroll,
	useSpring,
	useTransform,
} from "motion/react";
import { FiArrowRight } from "react-icons/fi";
import portrait480 from "../../assets/images/portrait-bw-480.webp";
import portrait880 from "../../assets/images/portrait-bw-880.webp";
import heroLocalization from "./localization";
import InkedImage from "../shared/inked-image";
import { calligraphy } from "../shared/calligraphy-images";
import { reveal } from "../shared/section-heading";
import { socials } from "../shared/links";
import { useReady } from "../../boot";

const { welcomeMessage, name, arabicName, description } = heroLocalization;

const C = 500; // centre of the 1000x1000 viewBox
const polar = (r, deg) => {
	const a = ((deg - 90) * Math.PI) / 180;
	return [C + r * Math.cos(a), C + r * Math.sin(a)];
};
const arc = (r, from, to) => {
	const [x0, y0] = polar(r, from);
	const [x1, y1] = polar(r, to);
	return `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 ${to - from > 180 ? 1 : 0} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
};

// Each ring is a partial arc that keeps turning. Speeds and directions differ
// so the bands slide past one another.
const rings = [
	{ kind: "lines", r: 316, span: [20, 250], count: 5, gap: 4, speed: 38 },
	{ kind: "lines", r: 354, span: [-60, 150], count: 3, gap: 6, speed: -64 },
	{ kind: "brush", r: 398, span: [150, 300], width: 16, speed: 52 },
	{ kind: "lines", r: 398, span: [310, 470], count: 4, gap: 5, speed: 52 },
	{ kind: "lines", r: 438, span: [10, 190], count: 5, gap: 5, speed: -96 },
	{ kind: "lines", r: 474, span: [200, 330], count: 6, gap: 4, speed: 120 },
];

const Diamond = ({ r, deg, size = 9 }) => {
	const [x, y] = polar(r, deg);
	return <rect x={x - size / 2} y={y - size / 2} width={size} height={size} transform={`rotate(45 ${x} ${y})`} />;
};

/**
 * One ring per SVG. The SVG element itself is rotated with a CSS animation,
 * which the browser composites on the GPU instead of repainting each frame.
 */
const Ring = ({ ring, index, ready }) => {
	const reduce = useReducedMotion();
	const [from, to] = ring.span;
	const gold = `halo-gold-${index}`;
	// CSS stroke draw-in (no per-element measuring like an animated SVG component).
	const draw = (delay) => ({ pathLength: 1, className: "ring-draw", style: { animationDelay: `${delay}s` } });

	return (
		<m.div
			className="absolute inset-0"
			initial={{ opacity: 0, rotate: ring.speed > 0 ? -40 : 40, scale: 0.92 }}
			animate={ready ? { opacity: 1, rotate: 0, scale: 1 } : undefined}
			transition={{ duration: 2.2, delay: 0.3 + index * 0.1, ease: [0.22, 1, 0.36, 1] }}
		>
			<svg
				viewBox="0 0 1000 1000"
				className="ring-spin grunge absolute inset-0 h-full w-full overflow-visible will-change-transform"
				style={{
					animation: reduce
						? undefined
						: `spin ${Math.abs(ring.speed)}s linear infinite ${ring.speed < 0 ? "reverse" : "normal"}`,
				}}
			>
				<defs>
					<linearGradient id={gold} x1="0" y1="0" x2="1" y2="1">
						<stop offset="0" stopColor="#9a7a34" />
						<stop offset="0.45" stopColor="#f6e2a4" />
						<stop offset="0.7" stopColor="#d4af5f" />
						<stop offset="1" stopColor="#8f6f2c" />
					</linearGradient>
				</defs>
				{ring.kind === "brush" ? (
					<path
						d={arc(ring.r, from, to)}
						fill="none"
						stroke={`url(#${gold})`}
						strokeOpacity="0.55"
						strokeWidth={ring.width}
						{...draw(0.6)}
					/>
				) : (
					Array.from({ length: ring.count }, (_, i) => (
						<path
							key={i}
							d={arc(ring.r - (ring.count / 2 - i) * ring.gap, from + i * 3, to - i * 5)}
							fill="none"
							stroke={`url(#${gold})`}
							strokeWidth={i % 2 ? 0.8 : 1.6}
							strokeLinecap="round"
							{...draw(0.5 + index * 0.12 + i * 0.05)}
						/>
					))
				)}
				<g fill="#f1d58a">
					<Diamond r={ring.r} deg={to + 6} size={7} />
					{ring.kind === "brush" && <Diamond r={ring.r} deg={from - 6} size={6} />}
				</g>
			</svg>
		</m.div>
	);
};

const Halo = ({ scrollTurn, ready }) => (
	<m.div className="absolute inset-0 will-change-transform" aria-hidden="true" style={{ rotate: scrollTurn }}>
		{rings.map((ring, i) => (
			<Ring key={i} ring={ring} index={i} ready={ready} />
		))}
	</m.div>
);

/**
 * The hero stays pinned while the first stretch of scrolling wipes it away on a
 * slanted, gold-lit edge, revealing the calligraphy wall before About rises.
 *
 * The wipe is transform-only (no clip-path repaints): an oversized outer frame,
 * rotated and slid upwards, clips the hero, while an inner frame applies the
 * exact inverse so the hero itself never moves. Both frames share a centre, so
 * the two rotations cancel, and at rest both are identity (crisp text).
 */
const useDiagonalWipe = (progress) => {
	const angle = useTransform(progress, [0, 0.12], [0, 6]);
	const travel = useTransform(progress, [0.12, 1], [0, 1]);
	return {
		outer: useMotionTemplate`translateY(calc(${travel} * -1 * (100vh + 24vw))) rotate(${angle}deg)`,
		inner: useMotionTemplate`rotate(calc(${angle} * -1deg)) translateY(calc(${travel} * (100vh + 24vw)))`,
		edge: useTransform(progress, [0.1, 0.2, 0.92, 1], [0, 1, 1, 0]),
		scale: useTransform(progress, [0.1, 1], [1, 0.92]),
		dim: useTransform(progress, [0.1, 1], [1, 0.35]),
	};
};

const Hero = () => {
	const ref = useRef(null);
	const reduce = useReducedMotion();
	const ready = useReady();
	// 0 while the hero fills the screen, 1 once it has been wiped away.
	const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
	const scrollTurn = useTransform(scrollYProgress, [0, 1], [0, 70]);
	const haloScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
	const haloOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
	const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
	const wipe = useDiagonalWipe(scrollYProgress);
	// Once fully wiped away, stop rendering the hero (its rings keep spinning otherwise).
	const [gone, setGone] = useState(false);
	useMotionValueEvent(scrollYProgress, "change", (v) => setGone(v >= 0.999));

	const mx = useMotionValue(0);
	const my = useMotionValue(0);
	const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 60, damping: 20 });
	const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 60, damping: 20 });

	const rectRef = useRef(null);
	const onPointerEnter = (e) => {
		rectRef.current = e.currentTarget.getBoundingClientRect();
	};
	const onPointerMove = (e) => {
		if (reduce || e.pointerType !== "mouse") return;
		const rect = rectRef.current;
		if (!rect) return;
		mx.set((e.clientX - rect.left) / rect.width - 0.5);
		my.set((e.clientY - rect.top) / rect.height - 0.5);
	};

	return (
		<div id="top" ref={ref} className="relative h-[165svh] -mb-[45svh]">
			<div className={`sticky top-0 h-[100svh] overflow-hidden will-change-transform ${gone ? "invisible" : ""}`}>
				<m.div
					className="absolute -left-[20%] -top-[12vw] h-[calc(100%+24vw)] w-[140%] overflow-hidden will-change-transform"
					style={{ transform: wipe.outer }}
				>
					<m.div
						className="absolute left-[14.2857%] top-[12vw] h-[calc(100%-24vw)] w-[71.4286%] will-change-transform"
						style={{ transform: wipe.inner }}
					>
						<m.section
							onPointerEnter={onPointerEnter}
							onPointerMove={onPointerMove}
							style={{ scale: wipe.scale, opacity: wipe.dim }}
							className="m-page relative mx-auto grid min-h-[100svh] max-w-7xl content-center items-center gap-1 overflow-hidden px-4 pb-12 pt-20 sm:gap-6 sm:pb-16 sm:pt-24 md:px-10 lg:grid-cols-[1fr_1.1fr] lg:gap-4 lg:overflow-visible lg:pt-20"
						>
							<m.div className="relative z-10 order-2 text-center will-change-transform lg:order-1 lg:text-left" style={{ y: textY }}>
								<InkedImage
									{...calligraphy.name}
									alt={`${arabicName}, ${name}`}
									priority
									onMount
									play={ready}
									sizes="(min-width: 1024px) min(40vw, 580px), min(74vw, 500px)"
									delay={0.2}
									duration={2.6}
									className="mx-auto block h-auto w-[min(74vw,500px)] short:w-[min(62vw,500px)] lg:mx-0 lg:-ml-2 lg:w-[min(40vw,580px)]"
								/>
								<m.p className="eyebrow mt-2" variants={reveal} initial="hidden" animate={ready ? "visible" : "hidden"} custom={1}>
									{welcomeMessage}
								</m.p>
								<m.h1
									className="mt-2 font-display text-[2.4rem] font-semibold leading-[1.05] tracking-tight text-parchment sm:mt-3 sm:text-5xl md:text-7xl"
									variants={reveal}
									initial="hidden"
									animate={ready ? "visible" : "hidden"}
									custom={2}
								>
									{name}
								</m.h1>
								<m.p
									className="mx-auto mt-4 max-w-md text-[15px] font-light leading-relaxed text-parchment-dim sm:mt-6 sm:max-w-lg sm:text-base md:text-lg lg:mx-0"
									variants={reveal}
									initial="hidden"
									animate={ready ? "visible" : "hidden"}
									custom={3}
								>
									{description}
								</m.p>
								<m.div
									className="mt-7 flex flex-wrap items-center justify-center gap-3 short:mt-5 sm:mt-9 lg:justify-start"
									variants={reveal}
									initial="hidden"
									animate={ready ? "visible" : "hidden"}
									custom={4}
								>
									<a href="#contact" className="btn-primary group">
										Get In Touch
										<FiArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
									</a>
									<a href="#projects" className="btn-secondary">
										View Work
									</a>
								</m.div>
								<m.div
									className="mt-7 flex items-center justify-center gap-3 short:hidden sm:mt-10 lg:justify-start"
									variants={reveal}
									initial="hidden"
									animate={ready ? "visible" : "hidden"}
									custom={5}
								>
									{socials.map(({ label, href, Icon }) => (
										<a
											key={label}
											href={href}
											target="_blank"
											rel="noreferrer"
											aria-label={label}
											className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-ink-800/80 text-parchment/80 transition-all duration-300 hover:-translate-y-0.5 hover:border-gold/50 hover:text-gold-light"
										>
											<Icon className="h-4 w-4" />
										</a>
									))}
								</m.div>
							</m.div>

							<m.div
								className="relative order-1 mx-auto aspect-square will-change-transform w-[min(62vw,340px)] short:w-[min(46vw,240px)] sm:w-[min(70vw,560px)] lg:order-2 lg:w-[min(52vw,720px)]"
								style={{ scale: haloScale, opacity: haloOpacity, rotateX, rotateY, transformPerspective: 1200 }}
							>
								<Halo scrollTurn={scrollTurn} ready={ready} />
								<m.div
									className="absolute left-1/2 top-1/2 aspect-square w-[56%] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full"
									initial={{ clipPath: "circle(0% at 50% 50%)" }}
									animate={ready ? { clipPath: "circle(50% at 50% 50%)" } : undefined}
									transition={{ duration: 1.6, delay: 0.3, ease: [0.65, 0, 0.35, 1] }}
								>
									<m.img
										src={portrait880}
										srcSet={`${portrait480} 480w, ${portrait880} 880w`}
										sizes="(min-width: 1024px) 400px, 48vw"
										width="880"
										height="879"
										fetchpriority="high"
										decoding="async"
										alt="Portrait of Mohammad Chreif"
										className="h-full w-full object-cover object-[50%_32%]"
										initial={{ scale: 1.35 }}
										animate={ready ? { scale: 1.05 } : undefined}
										transition={{ duration: 2.4, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
									/>
									<div className="absolute inset-0 rounded-full bg-[radial-gradient(circle,transparent_58%,rgba(11,10,8,0.55)_100%)]" />
									<div className="absolute inset-0 rounded-full ring-1 ring-inset ring-gold/40" />
								</m.div>
							</m.div>
						</m.section>
					</m.div>
					{/* The wipe's leading edge, lit gold */}
					<m.div
						className="absolute inset-x-0 bottom-0 h-[2px] bg-gradient-to-r from-transparent via-gold-light to-transparent shadow-[0_0_28px_4px_rgba(241,213,138,0.45)]"
						style={{ opacity: wipe.edge }}
					/>
				</m.div>
			</div>
		</div>
	);
};

export default Hero;
